import React, { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebaseConfig"; // Firestore and Storage imports
import "./Admin.css";
import { FaImage, FaEdit, FaTrashAlt } from "react-icons/fa";

const Admin = () => {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    category: "",
    description: "",
    name: "",
    price: "",
    remainingStock: "",
    images: [], // Pentru fișierele de încărcat
    imageURL: [],
  });
  const [editId, setEditId] = useState(null); // Track which product is being edited
  const [isDialogOpen, setIsDialogOpen] = useState(false); // Control dialog visibility
  const [productToDelete, setProductToDelete] = useState(null); // Product to delete

  // Fetch products from Firestore
  useEffect(() => {
    const fetchProducts = async () => {
      const querySnapshot = await getDocs(collection(db, "Products"));
      const productsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setProducts(productsData);
    };
    fetchProducts();
  }, []);

  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  // Handle file selection for images
  const handleFileChange = (e) => {
    // Transformă FileList într-un array și salvează-l în state
    const filesArray = Array.from(e.target.files);
    setForm({ ...form, images: filesArray });
  };

  // Add or update a product
  // Add or update a product
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let imageUrls = editId
        ? products.find((p) => p.id === editId).imageURL
        : []; // Păstrează imaginile existente în caz de editare

      // Încarcă noi imagini dacă există
      if (form.images && form.images.length > 0) {
        for (const file of form.images) {
          const storageRef = ref(storage, `products/${file.name}`);
          await uploadBytes(storageRef, file);
          const downloadURL = await getDownloadURL(storageRef);
          imageUrls.push(downloadURL); // Adaugă URL-urile noi
        }
      }

      const { images, ...dataToSend } = form;
      dataToSend.imageURL = imageUrls;

      if (editId) {
        const productRef = doc(db, "Products", editId);
        await updateDoc(productRef, dataToSend);
        setEditId(null);
        console.log("Product updated successfully!");
      } else {
        await addDoc(collection(db, "Products"), dataToSend);
        console.log("Product added successfully!");
      }

      // Resetează formularul
      setForm({
        category: "",
        description: "",
        name: "",
        price: "",
        remainingStock: "",
        images: [],
      });

      // Actualizează lista de produse
      const querySnapshot = await getDocs(collection(db, "Products"));
      const productsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setProducts(productsData);
    } catch (error) {
      console.error("Error adding/updating product:", error);
    }
  };

  // Remove an image from the current list
  const removeImage = (urlToRemove) => {
    console.log("Removing image:", urlToRemove);

    // Actualizează form.imageURL
    const updatedImageURLs = form.imageURL.filter((url) => url !== urlToRemove);
    setForm({
      ...form,
      imageURL: updatedImageURLs,
    });

    // Actualizează imaginea și în lista de produse (pentru vizualizarea în tabel)
    if (editId) {
      setProducts((prevProducts) =>
        prevProducts.map((product) =>
          product.id === editId
            ? { ...product, imageURL: updatedImageURLs }
            : product
        )
      );
    }
  };

  // Delete a product
  const handleDelete = async () => {
    try {
      if (productToDelete) {
        await deleteDoc(doc(db, "Products", productToDelete.id));
        setProducts(
          products.filter((product) => product.id !== productToDelete.id)
        );
        setIsDialogOpen(false); // Close dialog after deletion
        console.log("Product deleted successfully!");
      }
    } catch (error) {
      console.error("Error deleting product:", error);
    }
  };

  // Open the delete confirmation dialog
  const openDeleteDialog = (product) => {
    setProductToDelete(product);
    setIsDialogOpen(true);
  };

  // Close the delete confirmation dialog
  const closeDeleteDialog = () => {
    setIsDialogOpen(false);
    setProductToDelete(null);
  };

  // Load product data into form for editing
  const handleEdit = (product) => {
    setEditId(product.id);
    setForm({
      category: product.category,
      description: product.description,
      name: product.name,
      price: product.price,
      remainingStock: product.remainingStock,
      images: [], // Fişierele noi selectate
      imageURL: product.imageURL, // URL-urile existente
    });
  };

  return (
    <div className="admin">
      <h1>Administrarea Produselor</h1>

      {/* Form for adding/updating products */}
      <form onSubmit={handleSubmit} className="product-form">
        {/* Name and Category */}
        <div className="input-group">
          <div>
            <input
              type="text"
              name="name"
              placeholder="Nume produs"
              value={form.name}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <select
              name="category"
              value={form.category}
              onChange={handleChange}
              required
            >
              <option value="">Selectează Categorie</option>
              <option value="Ghiozdane">Ghiozdane</option>
              <option value="Poșete">Poșete</option>
              <option value="Genți Elegante">Genți Elegante</option>
              <option value="Borsete">Borsete</option>
              <option value="Altele">Altele</option>
            </select>
          </div>
        </div>

        {/* Description */}
        <textarea
          name="description"
          placeholder="Descriere"
          value={form.description}
          onChange={handleChange}
          required
          style={{ width: "100%" }}
        />

        {/* Price and Remaining Stock */}
        <div className="input-group">
          <div>
            <input
              type="number"
              name="price"
              placeholder="Preț"
              value={form.price}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <input
              type="number"
              name="remainingStock"
              placeholder="Stoc disponibil"
              value={form.remainingStock}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="file-input">
          <label htmlFor="fileUpload" className="file-label">
            <FaImage />
            Încarcă poze
          </label>
          <input
            id="fileUpload"
            type="file"
            multiple
            onChange={handleFileChange}
            className="file-input-hidden"
          />
          <span className="file-chosen">
            {form.images.length > 0
              ? `${form.images.length} fișiere selectate`
              : "Nicio fotografie aleasă."}
          </span>
        </div>

        <div className="button-container">
          <button type="submit" className="add-product-button">
            <span className="icon">+</span>
            {editId ? "Actualizează" : "Adaugă"} Produs
          </button>
        </div>
        <div className="spacing"></div>
      </form>

      {/* Dialog for delete confirmation */}
      {isDialogOpen && (
        <div className="delete-dialog">
          <div className="dialog-content">
            <h3>{`Sunteți sigur că doriți să ștergeți produsul "${productToDelete.name}"?`}</h3>
            <div className="dialog-actions">
              <button onClick={handleDelete}>DA</button>
              <button onClick={closeDeleteDialog}>NU</button>
            </div>
          </div>
        </div>
      )}

      {/* Titlul pentru Produse existente */}
      <h2 className="products-title">Produse existente:</h2>

      {/* List of products */}
      <div className="table-container">
        <table className="product-table">
          <thead>
            <tr>
              <th>Nume</th>
              <th>Categorie</th>
              <th>Descriere</th>
              <th>Preț</th>
              <th>Stoc</th>
              <th>Imagini</th>
              <th>Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr key={product.id}>
                <td>{product.name}</td>
                <td>{product.category}</td>
                <td>{product.description}</td>
                <td>{product.price}</td>
                <td>{product.remainingStock}</td>
                <td>
                  {product.imageURL.map((url, index) => (
                    <div
                      key={index}
                      style={{ display: "inline-block", marginRight: "5px" }}
                    >
                      <img
                        src={url}
                        alt={`product-${index}`}
                        style={{ width: "50px", height: "50px" }}
                      />
                      <button
                        onClick={() => removeImage(url)}
                        className="delete-image-button"
                      >
                        <FaTrashAlt style={{ fontSize: "24px" }} />
                      </button>
                    </div>
                  ))}
                </td>

                <td>
                  <div className="button-container">
                    <button
                      className="edit-btn"
                      onClick={() => handleEdit(product)}
                    >
                      <FaEdit style={{ marginRight: "5px" }} /> Editează
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => openDeleteDialog(product)}
                    >
                      <FaTrashAlt style={{ marginRight: "5px" }} /> Șterge
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Admin;
